// The load curve strip: today's demand vs. what the plants can make, a "now" marker,
// and tomorrow's demand ghosted behind. Red where supply falls short.
import type { Balance, MapData, World } from '../sim';
import { homeDemandPerTick, plantOutputPerTick } from '../sim';

export interface CurveStrip {
  update(world: World): void;
}

const SAMPLES = 96;

export function createCurveStrip(parent: HTMLElement, map: MapData, balance: Balance): CurveStrip {
  const wrap = document.createElement('div');
  wrap.className = 'curve';
  const canvas = document.createElement('canvas');
  wrap.appendChild(canvas);
  const label = document.createElement('div');
  label.className = 'curve-label';
  label.textContent = 'today · demand vs supply';
  wrap.appendChild(label);
  parent.appendChild(wrap);
  const ctx = canvas.getContext('2d');

  let lastKey = '';
  let cache: { demand: number[]; supply: number[]; tomorrow: number[]; max: number } | null = null;

  function sample(world: World): void {
    const live = world.homes.filter((h) => h.status !== 'dark').length;
    const tph = balance.time.ticksPerHour;
    const demand: number[] = [];
    const supply: number[] = [];
    const tomorrow: number[] = [];
    let max = 1e-9;
    const probe: World = { ...world, time: { ...world.time } };
    for (let i = 0; i <= SAMPLES; i++) {
      const hour = (i / SAMPLES) * 24;
      probe.time.hour = hour;
      const d = live * homeDemandPerTick(hour, balance) * tph;
      let s = 0;
      for (const p of world.plants) s += plantOutputPerTick(p, probe, map, balance) * tph;
      demand.push(d);
      supply.push(s);
      tomorrow.push(d * (1 + map.spawn.growthPerDay));
      max = Math.max(max, d, s, tomorrow[i] ?? 0);
    }
    cache = { demand, supply, tomorrow, max: max * 1.15 };
  }

  function draw(world: World): void {
    if (!ctx || !cache) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const w = wrap.clientWidth;
    const h = wrap.clientHeight - 16;
    if (canvas.width !== w * dpr || canvas.height !== h * dpr) {
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);
    const { demand, supply, tomorrow, max } = cache;
    const X = (i: number) => (i / SAMPLES) * w;
    const Y = (v: number) => h - (v / max) * (h - 6) - 2;

    // Night bands.
    ctx.fillStyle = 'rgba(20,26,51,0.06)';
    ctx.fillRect(0, 0, X(SAMPLES / 4), h);
    ctx.fillRect(X((19 / 24) * SAMPLES), 0, w, h);

    // Shortfall fill.
    ctx.beginPath();
    let open = false;
    for (let i = 0; i <= SAMPLES; i++) {
      const d = demand[i] ?? 0;
      const s = supply[i] ?? 0;
      if (d > s) {
        if (!open) {
          ctx.moveTo(X(i), Y(s));
          open = true;
        }
        ctx.lineTo(X(i), Y(d));
      } else if (open) {
        ctx.lineTo(X(i), Y(s));
        for (let j = i; j >= 0 && (demand[j] ?? 0) > (supply[j] ?? 0); j--)
          ctx.lineTo(X(j), Y(supply[j] ?? 0));
        ctx.closePath();
        open = false;
      }
    }
    if (open) {
      for (let j = SAMPLES; j >= 0 && (demand[j] ?? 0) > (supply[j] ?? 0); j--)
        ctx.lineTo(X(j), Y(supply[j] ?? 0));
      ctx.closePath();
    }
    ctx.fillStyle = 'rgba(214,69,69,0.35)';
    ctx.fill();

    // Tomorrow ghost.
    ctx.beginPath();
    for (let i = 0; i <= SAMPLES; i++) ctx.lineTo(X(i), Y(tomorrow[i] ?? 0));
    ctx.strokeStyle = 'rgba(43,42,39,0.22)';
    ctx.setLineDash([3, 3]);
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.setLineDash([]);

    // Demand.
    ctx.beginPath();
    for (let i = 0; i <= SAMPLES; i++) ctx.lineTo(X(i), Y(demand[i] ?? 0));
    ctx.strokeStyle = '#2b2a27';
    ctx.lineWidth = 1.6;
    ctx.stroke();

    // Supply.
    ctx.beginPath();
    for (let i = 0; i <= SAMPLES; i++) ctx.lineTo(X(i), Y(supply[i] ?? 0));
    ctx.strokeStyle = '#f2b134';
    ctx.lineWidth = 1.6;
    ctx.stroke();

    // Now.
    const nx = (world.time.hour / 24) * w;
    ctx.strokeStyle = '#1f6feb';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(nx, 0);
    ctx.lineTo(nx, h);
    ctx.stroke();
  }

  return {
    update(world) {
      const live = world.homes.filter((h) => h.status !== 'dark').length;
      const key = `${world.time.day}|${live}|${world.plants.length}|${world.weather.cloud}|${world.weather.windy}`;
      if (key !== lastKey) {
        lastKey = key;
        sample(world);
      }
      draw(world);
    },
  };
}
